import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { ArrowLeft, Calendar, Crown, Loader2, Mail, RefreshCw, Sparkles, Users, X, Check } from "lucide-react";

const fmtChf = (n) => `CHF ${Math.round(n || 0).toLocaleString("de-CH")}`;

const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("de-CH", { day: "2-digit", month: "short", year: "numeric" });
  } catch (e) { return s; }
};

const WaitlistDrawer = ({ slug, onClose, onChanged }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    api.get(`/events/admin/${slug}/waitlist`).then((r) => setItems(r.data));
  }, [slug]);

  const notify = async (wlId) => {
    if (!window.confirm("Wartelisten-Person jetzt per Mail benachrichtigen?")) return;
    setBusy((b) => ({ ...b, [wlId]: true }));
    try {
      await api.post(`/events/admin/${slug}/waitlist/${wlId}/notify`, {});
      const r = await api.get(`/events/admin/${slug}/waitlist`);
      setItems(r.data);
      if (onChanged) onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setBusy((b) => ({ ...b, [wlId]: false }));
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-stretch justify-end bg-black/65 backdrop-blur-sm" onClick={onClose} data-testid="waitlist-drawer">
      <div className="w-full max-w-xl bg-[#0E1A30] border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-white/10 bg-[#0E1A30]/95 backdrop-blur">
          <div>
            <div className="overline text-[#a48cff]">Warteliste</div>
            <h2 className="serif text-2xl font-light mt-1">{slug}</h2>
          </div>
          <button onClick={onClose} className="text-white/55 hover:text-white p-2" data-testid="waitlist-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {items === null && <div className="text-center py-12 text-white/55"><Loader2 className="animate-spin mx-auto" /></div>}
          {items && items.length === 0 && <div className="text-center py-12 text-white/55">Noch niemand auf der Warteliste.</div>}
          {items && items.map((w) => (
            <div key={w.id} className={`rounded-xl border ${w.notified ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.02]"} p-4`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="serif text-base">{w.first_name}</div>
                  <div className="text-xs text-white/55">{w.email} {w.phone ? `· ${w.phone}` : ""}</div>
                  <div className="text-xs text-white/35 mt-1">Eingetragen: {fmtDate(w.created_at)}</div>
                  {w.message && <p className="text-sm text-white/75 mt-2 italic">&bdquo;{w.message}&ldquo;</p>}
                </div>
                <div className="text-right shrink-0">
                  {w.notified ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-300"><Check size={12} /> Benachrichtigt</span>
                  ) : (
                    <button onClick={() => notify(w.id)} disabled={!!busy[w.id]}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#7a5cf0] text-white text-xs hover:bg-[#6a4ce0] disabled:opacity-60"
                      data-testid={`notify-${w.id}`}>
                      {busy[w.id] ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Benachrichtigen
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const EventsPipeline = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setBusy(true);
    try {
      const r = await api.get("/events/admin/pipeline");
      setData(r.data);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { nav("/admin/login"); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  if (loading || !user) return <div className="pt-32 text-center text-white/55">Lade…</div>;

  const totalPipeline = (data || []).reduce((s, e) => s + (e.pipeline_chf || 0), 0);
  const totalPotential = (data || []).reduce((s, e) => s + (e.potential_chf || 0), 0);
  const totalWaitlist = (data || []).reduce((s, e) => s + (e.waitlist_count || 0), 0);

  return (
    <div className="pt-24 min-h-screen" data-testid="events-pipeline">
      <div className="container-sat py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/admin" className="btn-ghost text-sm"><ArrowLeft size={14} /> Admin</Link>
            <h1 className="headline-xl text-4xl mt-3">Events · Pipeline</h1>
            <p className="serif italic text-white/55 mt-2">Reservierungen, Warteliste und Umsatz aller Premium-Events auf einen Blick.</p>
          </div>
          <button onClick={reload} disabled={busy} className="btn-secondary text-sm">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aktualisieren
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid sm:grid-cols-3 gap-3 mt-8" data-testid="pipeline-kpis">
          <div className="glass rounded-2xl p-5">
            <div className="overline text-[#D4AF37]">Bezahlt</div>
            <div className="serif text-3xl mt-2">{fmtChf(totalPipeline)}</div>
            <div className="text-xs text-white/40 mt-1">Confirmed Pipeline</div>
          </div>
          <div className="glass rounded-2xl p-5">
            <div className="overline text-[#D4AF37]">Potenzial</div>
            <div className="serif text-3xl mt-2">{fmtChf(totalPotential)}</div>
            <div className="text-xs text-white/40 mt-1">Wenn alle Events ausverkauft</div>
          </div>
          <div className="glass rounded-2xl p-5">
            <div className="overline text-[#a48cff]">Warteliste</div>
            <div className="serif text-3xl mt-2">{totalWaitlist}</div>
            <div className="text-xs text-white/40 mt-1">Aktive Wartende über alle Events</div>
          </div>
        </div>

        {/* Per-event table */}
        <div className="mt-10 space-y-3" data-testid="pipeline-events">
          {data === null && <div className="text-center py-16 text-white/55"><Loader2 className="animate-spin mx-auto" /></div>}
          {data && data.length === 0 && <div className="text-center py-16 text-white/55">Keine bevorstehenden Events.</div>}
          {data && data.map((e) => (
            <div key={e.slug}
              className={`rounded-2xl border p-5 grid lg:grid-cols-12 gap-4 items-center ${e.is_exclusive ? "border-[#D4AF37]/30" : "border-white/10"} ${e.is_sold_out ? "bg-[#7a5cf0]/5" : "bg-white/[0.02]"}`}
              data-testid={`pipeline-row-${e.slug}`}>
              <div className="lg:col-span-4">
                <div className="flex items-center gap-2 flex-wrap">
                  {e.is_exclusive && <span className="inline-flex items-center gap-1 text-[10px] text-[#D4AF37] uppercase tracking-widest"><Crown size={10} /> Exklusiv</span>}
                  {e.is_sold_out && <span className="inline-flex items-center gap-1 text-[10px] text-[#a48cff] uppercase tracking-widest"><Sparkles size={10} /> Sold-out</span>}
                </div>
                <div className="serif text-lg mt-1">{e.title}</div>
                <div className="text-xs text-white/45 mt-1 flex items-center gap-1"><Calendar size={11} /> {fmtDate(e.starts_at)}{e.ends_at && e.ends_at !== e.starts_at ? ` – ${fmtDate(e.ends_at)}` : ""}</div>
              </div>

              <div className="lg:col-span-3 flex items-center gap-4">
                <div className="text-sm">
                  <div className="text-white/40 text-xs">Belegung</div>
                  <div className="text-white"><b>{e.total_booked}</b> / {e.capacity}</div>
                </div>
                <div className="flex-1">
                  <div className="h-2 bg-white/[0.05] rounded-full overflow-hidden">
                    <div className={`h-full ${e.is_sold_out ? "bg-[#7a5cf0]" : "bg-[#D4AF37]"}`}
                      style={{ width: `${e.capacity ? Math.min(100, (e.total_booked / e.capacity) * 100) : 0}%` }} />
                  </div>
                  <div className="text-[10px] text-white/40 mt-1">{e.spots_left} verfügbar</div>
                </div>
              </div>

              <div className="lg:col-span-2 text-sm">
                <div className="text-white/40 text-xs">Pipeline</div>
                <div className="serif text-lg text-[#D4AF37]">{fmtChf(e.pipeline_chf)}</div>
                <div className="text-[10px] text-white/40">+{fmtChf(e.potential_chf)} potenziell</div>
              </div>

              <div className="lg:col-span-2 text-sm">
                <div className="text-white/40 text-xs">Warteliste</div>
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-[#a48cff]" />
                  <span className="text-lg">{e.waitlist_count}</span>
                  {e.waitlist_pending > 0 && <span className="text-[10px] text-[#FFD274]">{e.waitlist_pending} offen</span>}
                </div>
              </div>

              <div className="lg:col-span-1 flex justify-end">
                <button onClick={() => setDrawer(e.slug)}
                  disabled={e.waitlist_count === 0}
                  className="text-xs px-3 py-2 rounded-full bg-[#7a5cf0]/15 border border-[#7a5cf0]/40 text-[#a48cff] hover:bg-[#7a5cf0]/25 disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`open-waitlist-${e.slug}`}>
                  Liste
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {drawer && <WaitlistDrawer slug={drawer} onClose={() => setDrawer(null)} onChanged={reload} />}
    </div>
  );
};

export default EventsPipeline;
